import React, { createContext, ReactNode, useContext, useState } from "react";

import { claimApi } from "@/main/api";
import { KeyValue } from "@/main/models/common";
import { ClaimSearchRequest } from "@/main/models/request";
import { ClaimSearchGridResponse } from "@/main/models/response";
import { PaginationEnum } from "@/main/services/enum/PaginationEnum";

interface ClaimContextType {
	dataGrid: ClaimSearchGridResponse[];
	formFilters: ClaimSearchRequest | null;
	setDataGrid: React.Dispatch<React.SetStateAction<ClaimSearchGridResponse[]>>;
	getDataSearchClaim: (filters?: ClaimSearchRequest | Partial<ClaimSearchRequest>, isResetSearch?: boolean) => void;
	updateStatusInGrid: (guidClaim: string, status: KeyValue) => void;
	refreshRowByGuidClaim: (guidClaim: string) => void;
	onResetSearch: () => void;
}

const ClaimContext = createContext<ClaimContextType | undefined>(undefined);

export const SearchClaimProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
	const [dataGrid, setDataGrid] = useState<ClaimSearchGridResponse[]>([]);
	const [formFilters, setFormFilters] = useState<ClaimSearchRequest | null>(null);

	function onResetSearch() {
		setDataGrid([]);
		setFormFilters(null);
	}

	async function getDataSearchClaim(
		filters: ClaimSearchRequest | Partial<ClaimSearchRequest> = {},
		isResetSearch: boolean = true,
	) {
		const request: ClaimSearchRequest = {
			...(isResetSearch ? {} : formFilters),
			...(filters as ClaimSearchRequest),
			pageIndex: PaginationEnum.PageIndexIntial,
			pageSize: PaginationEnum.PageSize30,
		};

		onResetSearch();
		setFormFilters(request);
	}

	function updateStatusInGrid(guidClaim: string, status: KeyValue) {
		const newDataGrid: ClaimSearchGridResponse[] = dataGrid.map((item) => {
			if (item.guidClaim === guidClaim) {
				return {
					...item,
					claimStatus: status.value,
					idClaimStatus: Number(status.key),
				};
			}
			return item;
		});
		setDataGrid(newDataGrid);
	}

	async function refreshRowByGuidClaim(guidClaim: string) {
		const findRow = dataGrid.find((item) => item.guidClaim === guidClaim);
		if (!findRow) return;

		const request: ClaimSearchRequest = {
			...(formFilters as ClaimSearchRequest),
			claimNumber: findRow.claimNumber,
			pageIndex: PaginationEnum.PageIndexIntial,
			pageSize: PaginationEnum.PageSize30,
		};

		const response = await claimApi.searchGrid(request);
		const {
			data: { results },
		} = response;

		if (results.length === 0) return;
		const findRowUpdated = results.find((item) => item.guidClaim === guidClaim);

		if (!findRowUpdated) return;

		const newDataGrid = dataGrid.map((item) => {
			if (item.guidClaim === guidClaim) {
				return findRowUpdated;
			}
			return item;
		});

		setDataGrid(newDataGrid);
	}

	return (
		<ClaimContext.Provider
			value={{
				dataGrid,
				formFilters,
				setDataGrid,
				refreshRowByGuidClaim,
				getDataSearchClaim,
				onResetSearch,
				updateStatusInGrid,
			}}
		>
			{children}
		</ClaimContext.Provider>
	);
};

export const useSearchClaimContext = (): ClaimContextType => {
	const context = useContext(ClaimContext);
	if (!context) {
		throw new Error("useClaimContext must be used within a ClaimProvider");
	}
	return context;
};
